import Icon from "@Components/generic/icon";
import { Events } from "@Context/common/general/context";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Alert, CircularProgress, IconButton, Skeleton } from "@mui/material";
import AccountRoles from "@Shared/enums/AccountRoles";
import { getRoleLevel } from "@Shared/utils/rolePermissions";
import { ReactNode, createContext, useContext, useState } from "react";
import { useOwnership } from "./ownership";

interface IPermissions {
  editor?: AccountRoles;
  viewer?: AccountRoles;
  children: ReactNode;
  /**
   * If true, allows the owner to edit regardless of their role
   * Only works when used within an Ownership component
   */
  ownerCanEdit?: boolean;
  /**
   * Optional component to render when the user doesn't have view permissions
   * If not provided, renders null when permissions are not met
   */
  fallback?: ReactNode;
}

interface PermissionsContextType {
  canView: boolean;
  canEdit: boolean;
  userRole: AccountRoles | null;
  isOwner: boolean;
}

const PermissionsContext = createContext<PermissionsContextType>({
  canView: true,
  canEdit: true,
  userRole: null,
  isOwner: false,
});

const FaultyAccountStateFallback = () => {
  const { i18n } = useI18n();
  const reloadAccountLabel = i18n("button.try-again") || "Reload account";
  const [loading, setLoading] = useState(false);

  const reloadAccount = () => {
    setLoading(true);
    Events.account.refreshCurrentAccount.emit();
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  return (
    <Alert
      severity="warning"
      action={
        <IconButton size="small" color="inherit" aria-label={reloadAccountLabel} onClick={reloadAccount} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : <Icon dsIcon="refresh" />}
        </IconButton>
      }
    >
      {i18n("account.error.reload") || "Something went wrong while loading your account. Please click the refresh button to try again."}
    </Alert>
  );
};

/**
 * Hook to access permissions in child components
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a Permissions component");
  }
  return context;
};

/**
 * A Higher Order Component that checks if the current user has sufficient role permissions
 * to view or edit content based on the specified role requirements.
 *
 * @param editor - The minimum role required to edit content
 * @param viewer - The minimum role required to view content
 * @param allowOwnerEdit - If true, allows the owner to edit regardless of their role
 * @param children - The child components to render if permissions are met
 * @param fallback - Optional component to render when permissions are not met
 */
const Permissions = ({ editor, viewer, ownerCanEdit = false, children, fallback }: IPermissions) => {
  const { globalState } = useGlobalContext();
  const isAccountResolved = globalState.account.is_resolved;
  const userRole = globalState.account?.user_role;
  const hasProducts = globalState.account?.data?.products?.length || 0 > 0;
  const hasAuthorizations = globalState.account?.authorizations?.length || 0 > 0;
  const ownershipContext = useOwnership?.();
  const isOwner = ownershipContext?.userId === globalState.user?.dbId || ownershipContext?.userId === globalState.user?.id;

  if (!isAccountResolved) {
    return <Skeleton variant="rounded" width="100%" height={50} />;
  }

  if (!userRole || !hasProducts || !hasAuthorizations) {
    return fallback || <FaultyAccountStateFallback />;
  }

  const userRoleLevel = getRoleLevel(userRole);

  // Calculate permissions
  const canEdit = (ownerCanEdit && isOwner) || !editor || userRoleLevel >= getRoleLevel(editor);
  const canView = canEdit || !viewer || userRoleLevel >= getRoleLevel(viewer);

  // If no view permissions, render fallback or null
  if (!canView) {
    return fallback || null;
  }

  return <PermissionsContext.Provider value={{ canView, canEdit, userRole, isOwner }}>{children}</PermissionsContext.Provider>;
};

export default Permissions;
